import { useEffect } from "react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SITE } from "@/lib/site";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    overline: "Legal",
    sections: [
      { h: "Information We Collect", p: "During reservation we collect personal details such as your name, contact number, government ID and address. This information is used strictly for guest verification, safety compliance and legal recording as required for accommodation records." },
      { h: "Third-Party Sharing", p: "Your private data is never sold or shared with external parties, except when required by law enforcement or as necessary for payment gateway processing of your booking." },
      { h: "Payment Security", p: "Financial transactions are processed via secure, encrypted payment channels. Credit card, UPI and bank details are never stored on property servers." },
      { h: "Data Protection", p: "Booking records are stored securely with access limited to authorized personnel. We retain only the information necessary to manage your reservation and meet legal obligations." },
      { h: "Contact", p: `For any privacy-related questions, contact us at ${SITE.email} or call ${SITE.phoneDisplay}.` },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    overline: "Legal",
    sections: [
      { h: "Payment Policy", p: "100% advance payment is compulsory to confirm a booking. Reservations without successful advance payment are not held or guaranteed." },
      { h: "Identity Verification", p: "All guests must present a valid physical photo ID (Aadhaar Card, Passport, Government ID or Driving License) upon arrival. Without valid documentation, room allocation will be refused without a refund." },
      { h: "Check-in & Check-out", p: "Check-in time is as specified at the time of online booking, and guests must report at their designated time. Check-out is on a 24-hour basis." },
      { h: "Booking Window", p: "Bookings are open from the 2nd day up to the 60th day prior to the visit date. Once a booking is confirmed, check-in dates cannot be changed." },
      { h: "Guest Restrictions", p: "Unmarried couples and primary guests below 18 years of age are strictly not allowed. Pets are not permitted on the premises." },
      { h: "Property Care", p: "Guests are liable to pay charges or penalties determined by Shree Somnath Trust for any damage caused to room property during their stay." },
    ],
  },
  cancellation: {
    title: "Cancellation Policy",
    overline: "Legal",
    sections: [
      { h: "Free Cancellation Till Check-in Date", p: "Free cancellation is available till the check-in date. If a booking is cancelled for any reason within this window, 100% of your payment will be refunded to your source account." },
      { h: "How to Cancel", p: "All cancellations must be requested in writing or processed through the official portal. Cancellation requests via phone alone are not accepted." },
      { h: "No-Show", p: "No refund applies if a guest fails to check in on the scheduled date." },
      { h: "Questions", p: `For cancellation assistance, contact us at ${SITE.phoneDisplay} or ${SITE.email}.` },
    ],
  },
  refund: {
    title: "Refund Policy",
    overline: "Legal",
    sections: [
      { h: "Refund Eligibility", p: "Bookings cancelled on or before the check-in date are eligible for a 100% refund of the advance payment, as per our Cancellation Policy. No refunds apply for no-shows." },
      { h: "Refund Method", p: "Refunds are processed to the original source account or payment method used at the time of booking." },
      { h: "Processing Time", p: "Once a cancellation is confirmed, please allow 7-10 working days for the refund to reflect in your source account." },
      { h: "Early Departure", p: "No refund is applicable for early departure or unused nights after check-in." },
      { h: "Refund Requests", p: `To request a refund, write to us at ${SITE.email} or call ${SITE.phoneDisplay} with your booking ID.` },
    ],
  },
};

export default function Legal({ page }) {
  const c = CONTENT[page];

  useEffect(() => {
    document.title = `${c.title} | Sagar Darshan Guest House, Somnath`;
  }, [c]);

  return (
    <div data-testid={`${page}-page`}>
      <PageHero overline={c.overline} title={c.title} />
      <section className="mx-auto max-w-3xl px-5 py-20 lg:py-28">
        {c.sections.map((s, i) => (
          <Reveal key={s.h} delay={i * 0.06}>
            <div className="border-b border-navy/10 py-8 first:pt-0">
              <h2 className="font-display text-2xl text-navy">{s.h}</h2>
              <p className="mt-3 text-sm leading-relaxed text-navy/70">{s.p}</p>
            </div>
          </Reveal>
        ))}
        <p className="mt-10 text-xs text-navy/40">Last updated: July 2026 · {SITE.name}, {SITE.address}</p>
      </section>
    </div>
  );
}
